import React, { useState } from 'react';
import { Zap, Brain, Sparkles } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-hot-toast';

const REASONING_MODES = [
    {
        key: 'fast',
        label: 'Fast',
        sub: 'Quick, factual',
        description: 'Best for straightforward questions, simple document lookups, and general chat. Responses in ~5–8 seconds.',
        Icon: Zap,
        accent: 'text-orange-500'
    },
    {
        key: 'deep',
        label: 'Deep',
        sub: 'Complex reasoning',
        description: 'Best for detailed analysis, multi-document comparisons, and tasks that need careful thinking. Responses in ~30–45 seconds.',
        Icon: Brain,
        accent: 'text-blue-500'
    }
];

function JproIntelligenceSettings() {
    const { currentUser, updateUiPreferences } = useAuth();
    const serverDefault = currentUser?.uiPreferences?.default_reasoning_mode || null;
    const [saving, setSaving] = useState(false);

    const setDefault = async (modeKey) => {
        const next = serverDefault === modeKey ? null : modeKey;
        setSaving(true);
        if (next) localStorage.setItem('jpro_default_reasoning_mode', next);
        else localStorage.removeItem('jpro_default_reasoning_mode');

        const result = await updateUiPreferences({ default_reasoning_mode: next });
        setSaving(false);
        if (!result.success) {
            toast.error('Could not save default mode');
        } else {
            toast.success(next ? `Default set to ${next === 'fast' ? 'Fast' : 'Deep'}` : 'Default cleared');
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={18} className="text-blue-500" />
                    <h2 className="text-xl font-dm-serif font-semibold">JPRO Intelligence</h2>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mb-5">
                    Tune how JPRO answers you. These preferences apply across all your chats.
                </p>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-unna font-medium mb-1">Default reasoning mode</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mb-3">
                            New chats start in this mode. You can switch on the fly from the chat input.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {REASONING_MODES.map(({ key, label, sub, description, Icon, accent }) => {
                                const selected = serverDefault === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        disabled={saving}
                                        onClick={() => setDefault(key)}
                                        className={`text-left rounded-lg border-2 p-4 transition-all ${
                                            selected
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-700'
                                        } ${saving ? 'opacity-60 cursor-wait' : ''}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Icon size={16} className={accent} />
                                            <span className="font-rasa font-semibold text-gray-900 dark:text-gray-100">
                                                {label}
                                            </span>
                                            <span className="text-xs font-rasa text-gray-500 dark:text-gray-400">
                                                {sub}
                                            </span>
                                            {selected && (
                                                <span className="ml-auto text-[10px] font-rasa font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                                    Default
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm font-rasa text-gray-600 dark:text-gray-300">
                                            {description}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>

                        {!serverDefault && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa mt-3">
                                No default set — JPRO will start each chat in Fast.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default JproIntelligenceSettings;
